import { getWidthSrcSet } from '@uefadigital/panenka';
import useResizeObserver from 'beautiful-react-hooks/useResizeObserver';
import clsx from 'clsx';
import type { SyntheticEvent } from 'react';
import { useRef, useCallback, useMemo, useState, memo } from 'react';

import { useImgVisible } from './hooks/useImgVisible';

import type { MediaImage } from '@cobalt/utilities';
import { IS_BAD_NETWORK_DETECTED } from '@utilities/browser';
import { imgBaseUrl } from '@utilities/WindowContext';

const breakPointsDictionary = {
  0: 'sm',
  440: 'md',
  768: 'lg',
  960: 'xl',
  1440: 'xxl',
} as const;
const lazyloadClassBase = 'pflazyload-wrapper';

const defaultProps = {
  formats: ['0@w2', '440@s2', '768@s2', '960@w1', '1440@w1'],
  fallback: `${imgBaseUrl}/imgml/void.svg`,
  title: 'no title',
};

const defaultSize = IS_BAD_NETWORK_DETECTED ? 4 : 1;

export type PictureHelperProps = {
  formats?: string[];
  forceFree?: boolean;
  image?: MediaImage['image'];
  imageUrl?: string;
  fallback?: string;
  title?: string;
  cssClass?: string;
  className?: string;
  style?: { [key: string]: string };
  wrapperClassName?: string;
  onLoad?: (ev: SyntheticEvent<HTMLImageElement>) => void;
  onError?: (ev: SyntheticEvent<HTMLImageElement>) => void;
};

const PictureHelper = ({
  formats = defaultProps.formats,
  forceFree,
  image,
  imageUrl,
  fallback = defaultProps.fallback,
  title = defaultProps.title,
  className,
  style,
  wrapperClassName,
  onLoad,
  onError,
}: PictureHelperProps) => {
  const imgRef = useRef<HTMLImageElement>(null);
  const [isError, setIsError] = useState(false);
  const isVisible = useImgVisible(imgRef);
  const DOMRect = useResizeObserver(imgRef);

  const imgSize = useMemo(() => (DOMRect?.width ? Math.ceil(DOMRect.width / 10) * 10 : null), [DOMRect?.width]);

  const getCobaltImageUrls = useCallback(
    (ratio: string) => {
      if (!image) return '';

      if (forceFree && image.free) ratio = 'f';

      const ratioMap = {
        f: image.free,
        w: image.wide,
        s: image.square,
        p: image.portrait,
        u: image.ultraWide,
      } as const;

      const f: Record<string, string> = ratioMap[ratio.toLowerCase()] ?? image.custom;

      if (!f) return '';

      return f[`${ratio}${defaultSize}`] ?? f[ratio] ?? Object.values(f)?.[0];
    },
    [forceFree, image],
  );

  const getImageUrls = useCallback(
    (ratio: string, getFirst?: boolean) => {
      let srcSet = getWidthSrcSet(fallback, getFirst);

      if (isVisible && imgSize && !isError && (image || imageUrl)) {
        const src = image ? getCobaltImageUrls(ratio) : imageUrl?.replace('_w1', `_${ratio}${defaultSize}`);
        srcSet = getWidthSrcSet(src, getFirst);
      }

      return srcSet;
    },
    [fallback, getCobaltImageUrls, image, imageUrl, imgSize, isError, isVisible],
  );

  const sortedFormats = useMemo(
    () =>
      formats
        ?.map((value: string) => {
          const [breakpoint, format] = (value ?? '@').split('@');
          return {
            breakpoint: parseInt(breakpoint || '0'),
            format: (format || '').charAt(0),
          };
        })
        .toSorted((a, b) => a.breakpoint - b.breakpoint) ?? [],
    [formats],
  );

  const breakPoints = useMemo(() => {
    let lastFormat = '';
    return sortedFormats
      .map(({ breakpoint, format }) => {
        const ratio = forceFree && image?.free ? 'f' : format;

        if (lastFormat && lastFormat === ratio) return null;
        lastFormat = ratio;

        return {
          breakpoint,
          ratio,
        };
      })
      .filter(Boolean);
  }, [forceFree, image?.free, sortedFormats]);

  const lazyloadClasses = useMemo(() => {
    let lastFormat = '';
    return sortedFormats.reduce((result, { breakpoint, format }) => {
      const ratio = forceFree && image?.free ? 'f' : format;

      if (lastFormat && lastFormat === ratio) return result;
      lastFormat = ratio;

      const ratioClassMap = {
        w: `${lazyloadClassBase}-${breakPointsDictionary[breakpoint]}-wide`,
        s: `${lazyloadClassBase}-${breakPointsDictionary[breakpoint]}-square`,
        p: `${lazyloadClassBase}-${breakPointsDictionary[breakpoint]}-portrait`,
        u: `${lazyloadClassBase}-${breakPointsDictionary[breakpoint]}-ultrawide`,
        custom: `${lazyloadClassBase}-${breakPointsDictionary[breakpoint]}-custom`,
        f: `${lazyloadClassBase}-free`,
      } as const;

      const ratioClassName = ratioClassMap[ratio] ?? ratioClassMap['custom'];
      result.push(ratioClassName);

      return result;
    }, []);
  }, [forceFree, image?.free, sortedFormats]);

  const setImgError = (e: SyntheticEvent<HTMLImageElement> | Event | string) => {
    isVisible && setIsError(true);
    onError && typeof onError === 'function' && onError(e as SyntheticEvent<HTMLImageElement>);
  };

  const onImgLoad = (event: SyntheticEvent<HTMLImageElement> | Event) => {
    onLoad && typeof onLoad === 'function' && onLoad(event as SyntheticEvent<HTMLImageElement>);
  };

  if (!breakPoints.length) return null;

  const classes = clsx([wrapperClassName, lazyloadClassBase, ...lazyloadClasses]);
  const imgClasses = clsx({
    picture: true,
    lazyfallback: isError && !fallback,
    [className]: !!className,
  });

  return (
    <figure className={classes} style={style}>
      <picture>
        {breakPoints.map((breakpoint) => (
          <source
            key={`item_${breakpoint.breakpoint}`}
            media={`(min-width: ${breakpoint.breakpoint}px)`}
            srcSet={getImageUrls(breakpoint.ratio)}
            sizes={`${imgSize ?? 0}px`}
          />
        ))}
        <img
          ref={imgRef}
          alt={title}
          title={title}
          onLoad={onImgLoad}
          onError={setImgError}
          loading='lazy'
          className={imgClasses}
          src={getImageUrls(breakPoints.at(-1).ratio, true)}
          sizes={`${imgSize ?? 0}px`}
        />
      </picture>
    </figure>
  );
};

export default memo(PictureHelper);
